import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Box,
  CircularProgress,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../api';
import apiService from '../services/apiService';
import { labelForLeadSource } from '../constants/leadSources';
import DriverRevenueReportSection from '../components/DriverRevenueReportSection';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement, Filler);

export default function Reports() {
  const [from, setFrom] = useState(dayjs().startOf('year').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().endOf('year').format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [jobMargin, setJobMargin] = useState(null);
  const [leadConversion, setLeadConversion] = useState(null);
  const [outboundComms, setOutboundComms] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [driverRevenue, setDriverRevenue] = useState(null);

  const canViewJobMargin = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return ['super_admin', 'admin', 'manager'].includes(u?.role);
    } catch {
      return false;
    }
  }, []);

  const handleExportPdf = async () => {
    const token = localStorage.getItem('token');
    try {
      setExportingPdf(true);
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const res = await fetch(`${API_BASE_URL}reports/pdf?${qs.toString()}`, {
        headers: {
          Accept: 'application/pdf',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || j.message || `HTTP ${res.status}`);
        }
        const t = await res.text().catch(() => '');
        throw new Error((t || '').slice(0, 400) || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reports-${from || 'from'}-to-${to || 'to'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      window.alert(`PDF export failed: ${e.message || 'Unknown error'}`);
    } finally {
      setExportingPdf(false);
    }
  };

  useEffect(() => {
    const fetchReportsData = async () => {
      try {
        setLoading(true);
        const qs = `from=${from}&to=${to}`;
        const safeGet = (url) => apiService.get(url).catch(() => null);

        const [
          revenueExpenses,
          profit,
          invoiceStatus,
          expensesByCategory,
          topClients,
          topExpenses,
          ordersByStatus,
          ordersByMonth,
          ordersByDriver,
          completedOrdersByClient,
          marginReport,
          leadConvReport,
          outboundCommsReport,
          driverRevenueReport,
        ] = await Promise.all([
          safeGet(`reports/revenue-expenses?${qs}`),
          safeGet(`reports/profit?${qs}`),
          safeGet(`reports/invoice-status?${qs}`),
          safeGet(`reports/expenses-by-category?${qs}`),
          safeGet(`reports/top-clients?${qs}`),
          safeGet(`reports/top-expenses?${qs}`),
          safeGet(`reports/orders-by-status?${qs}`),
          safeGet(`reports/orders-by-month?${qs}`),
          safeGet(`reports/orders-by-driver?${qs}`),
          safeGet(`reports/completed-orders?${qs}`),
          canViewJobMargin ? safeGet(`reports/job-margin?${qs}`) : Promise.resolve(null),
          safeGet(`reports/lead-conversion?${qs}`),
          safeGet(`reports/outbound-comms?${qs}`),
          safeGet(`reports/driver-revenue?${qs}`),
        ]);

        console.log('Reports API responses:', {
          revenueExpenses,
          profit,
          invoiceStatus,
          expensesByCategory,
          topClients,
          topExpenses,
          ordersByStatus,
          ordersByMonth,
          ordersByDriver,
          completedOrdersByClient
        });

        setData({
          revenueExpenses,
          profit,
          invoiceStatus,
          expensesByCategory,
          topClients,
          topExpenses,
          ordersByStatus,
          ordersByMonth,
          ordersByDriver,
          completedOrdersByClient,
        });
        setJobMargin(marginReport);
        setLeadConversion(leadConvReport);
        setOutboundComms(outboundCommsReport);
        setDriverRevenue(driverRevenueReport);
      } catch (error) {
        console.error('Error fetching reports data:', error);
        if (error.message === 'Session expired') {
          // apiService already handled logout
          return;
        }
        // Set empty data on error
        setData({});
        setJobMargin(null);
        setLeadConversion(null);
        setOutboundComms(null);
        setDriverRevenue(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReportsData();
  }, [from, to, canViewJobMargin]);

  // Prepare chart data with safe defaults and shape mapping
  const revenueExpensesRaw = data.revenueExpenses || {};
  const invoicesArr = Array.isArray(revenueExpensesRaw.invoices) ? revenueExpensesRaw.invoices : [];
  const expensesArr = Array.isArray(revenueExpensesRaw.expenses) ? revenueExpensesRaw.expenses : [];
  const months = invoicesArr.map(i => i.month);
  const revenueData = invoicesArr.map(i => Number(i.revenue) || 0);
  const expensesData = expensesArr.map(e => Number(e.expenses) || 0);

  const profitArr = Array.isArray(data.profit) ? data.profit : [];
  const profitData = profitArr.map(p => Number(p.profit) || 0);

  const paidInvoices = data.invoiceStatus?.paid || 0;
  const unpaidInvoices = data.invoiceStatus?.unpaid || 0;

  const expensesByCategory = Array.isArray(data.expensesByCategory) ? data.expensesByCategory : [];
  const topClients = Array.isArray(data.topClients) ? data.topClients : [];
  const topExpenses = Array.isArray(data.topExpenses) ? data.topExpenses : [];
  const ordersByStatus = Array.isArray(data.ordersByStatus) ? data.ordersByStatus : [];

  const ordersByMonthArr = Array.isArray(data.ordersByMonth) ? data.ordersByMonth : [];
  const orderMonths = ordersByMonthArr.map(o => o.month);
  const ordersByMonth = ordersByMonthArr.map(o => o.count);

  const topDrivers = Array.isArray(data.ordersByDriver)
    ? data.ordersByDriver.map(d => ({ name: d.driver, orders: d.count }))
    : [];
  const completedOrdersByClient = Array.isArray(data.completedOrdersByClient)
    ? data.completedOrdersByClient.map(c => ({ name: c.client, orders: c.count }))
    : [];

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Typography variant="h3" sx={{ mb: 4, textAlign: 'center', fontWeight: 700, fontSize: { xs: '1.65rem', sm: '2.5rem' }, px: { xs: 0.5, sm: 0 } }}>
        Reports & Analytics
      </Typography>
      
      {/* Date Range Selector */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Select Date Range</Typography>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          gap: 2,
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'center',
          maxWidth: '100%',
        }}>
          <TextField 
            label="From" 
            type="date" 
            value={from} 
            onChange={e => setFrom(e.target.value)} 
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ minWidth: 0, maxWidth: { sm: 260 }, flex: { sm: '1 1 200px' } }}
          />
          <Typography variant="body1" sx={{ alignSelf: 'center', display: { xs: 'none', sm: 'block' } }}>to</Typography>
          <Typography variant="body2" sx={{ display: { xs: 'block', sm: 'none' }, color: 'text.secondary', textAlign: 'center' }}>to</Typography>
          <TextField 
            label="To" 
            type="date" 
            value={to} 
            onChange={e => setTo(e.target.value)} 
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ minWidth: 0, maxWidth: { sm: 260 }, flex: { sm: '1 1 200px' } }}
          />
          <Button
            variant="contained"
            onClick={handleExportPdf}
            disabled={exportingPdf}
            fullWidth
            sx={{
              ml: { xs: 0, sm: 0 },
              maxWidth: { sm: 220 },
              alignSelf: { sm: 'center' },
              fontWeight: 700,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
              },
            }}
          >
            {exportingPdf ? 'Exporting…' : 'Export PDF'}
          </Button>
        </Box>
      </Paper>

      {leadConversion?.totals ? (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Lead conversion
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={`Total: ${leadConversion.totals.total}`} />
            <Chip label={`Open: ${leadConversion.totals.open}`} variant="outlined" />
            <Chip label={`Won: ${leadConversion.totals.won}`} color="success" variant="outlined" />
            <Chip label={`Lost: ${leadConversion.totals.lost}`} variant="outlined" />
            <Chip
              label={`Win rate: ${leadConversion.totals.conversion_rate ?? '—'}%`}
              color="primary"
              variant="outlined"
            />
          </Box>
          {Array.isArray(leadConversion.by_source) && leadConversion.by_source.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Source</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Open</TableCell>
                    <TableCell align="right">Won</TableCell>
                    <TableCell align="right">Lost</TableCell>
                    <TableCell align="right">Win rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leadConversion.by_source.map((row) => (
                    <TableRow key={row.source}>
                      <TableCell>{labelForLeadSource(row.source)}</TableCell>
                      <TableCell align="right">{row.total}</TableCell>
                      <TableCell align="right">{row.open}</TableCell>
                      <TableCell align="right">{row.won}</TableCell>
                      <TableCell align="right">{row.lost}</TableCell>
                      <TableCell align="right">{row.conversion_rate ?? '—'}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No leads in this period.
            </Typography>
          )}
        </Paper>
      ) : null}

      {outboundComms?.totals ? (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Outbound messages (manual SMS / email)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={`Total sent: ${outboundComms.totals.total}`} />
            <Chip label={`SMS: ${outboundComms.totals.by_channel?.manual_sms ?? 0}`} variant="outlined" />
            <Chip label={`Email: ${outboundComms.totals.by_channel?.manual_email ?? 0}`} variant="outlined" />
          </Box>
          {Array.isArray(outboundComms.by_day) && outboundComms.by_day.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">SMS</TableCell>
                    <TableCell align="right">Email</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outboundComms.by_day.map((row) => (
                    <TableRow key={row.date}>
                      <TableCell>{row.date}</TableCell>
                      <TableCell align="right">{row.manual_sms ?? 0}</TableCell>
                      <TableCell align="right">{row.manual_email ?? 0}</TableCell>
                      <TableCell align="right">{row.total ?? 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No manual outbound messages in this period.
            </Typography>
          )}
        </Paper>
      ) : null}

      {canViewJobMargin && jobMargin?.totals ? (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Job margin
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={`Jobs: ${jobMargin.totals.count}`} />
            <Chip label={`Sell: ${jobMargin.totals.sell}`} color="primary" variant="outlined" />
            <Chip label={`Cost: ${jobMargin.totals.cost}`} variant="outlined" />
            <Chip
              label={`Margin: ${jobMargin.totals.margin} (${jobMargin.totals.margin_percent ?? 0}%)`}
              color={jobMargin.totals.margin >= 0 ? 'success' : 'error'}
              variant="outlined"
            />
          </Box>
          {Array.isArray(jobMargin.jobs) && jobMargin.jobs.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Sell</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Margin</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobMargin.jobs.slice(0, 25).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.client}</TableCell>
                      <TableCell>{row.scheduled_date}</TableCell>
                      <TableCell align="right">{row.sell_amount}</TableCell>
                      <TableCell align="right">{row.total_cost}</TableCell>
                      <TableCell align="right">
                        {row.margin} ({row.margin_percent ?? 0}%)
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No jobs with costing data in this period.
            </Typography>
          )}
        </Paper>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          <DriverRevenueReportSection report={driverRevenue} from={from} to={to} />

          <Divider sx={{ my: 4, borderWidth: 2 }} />

          {/* Financial Reports Section */}
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 3, 
                color: 'primary.main', 
                fontWeight: 'bold',
                textAlign: 'center',
                pb: 2,
                borderBottom: '3px solid',
                borderColor: 'primary.main'
              }}
            >
              💰 Financial Reports
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: 340, position: 'relative', overflow: 'hidden' }}>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, fontSize: '1.5rem' }}>Revenue & Expenses by Month</Typography>
                  <Bar
                    data={{
                      labels: months,
                      datasets: [
                        {
                          label: 'Revenue',
                          data: revenueData,
                          backgroundColor: 'rgba(16, 185, 129, 0.8)',
                          borderColor: '#10b981',
                          borderWidth: 2,
                        },
                        {
                          label: 'Expenses',
                          data: expensesData,
                          backgroundColor: 'rgba(239, 68, 68, 0.8)',
                          borderColor: '#ef4444',
                          borderWidth: 2,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top',
                          labels: { font: { size: 12 } }
                        },
                      },
                      scales: {
                        x: {
                          ticks: { font: { size: 11 }, maxRotation: 45, minRotation: 30 },
                        },
                        y: {
                          beginAtZero: true,
                          ticks: { font: { size: 11 } },
                        },
                      },
                    }}
                    height={300}
                  />
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: 340, position: 'relative', overflow: 'hidden' }}>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, fontSize: '1.5rem' }}>Profit by Month</Typography>
                  <Line
                    data={{
                      labels: months,
                      datasets: [
                        {
                          label: 'Profit',
                          data: profitData,
                          backgroundColor: 'rgba(59, 130, 246, 0.2)',
                          borderColor: '#3b82f6',
                          borderWidth: 3,
                          tension: 0.4,
                          fill: true,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top',
                          labels: { font: { size: 12 } }
                        },
                      },
                      scales: {
                        x: {
                          ticks: { font: { size: 11 }, maxRotation: 45, minRotation: 30 },
                        },
                        y: {
                          beginAtZero: true,
                          ticks: { font: { size: 11 } },
                        },
                      },
                    }}
                    height={300}
                  />
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, height: 340, position: 'relative', overflow: 'hidden' }}>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, fontSize: '1.5rem' }}>Paid / Unpaid Invoices</Typography>
                  <Pie
                    data={{
                      labels: ['Paid', 'Unpaid'],
                      datasets: [
                        {
                          data: [paidInvoices, unpaidInvoices],
                          backgroundColor: ['#10b981', '#ef4444'],
                          borderWidth: 2,
                          borderColor: '#fff',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: { font: { size: 12 } }
                        },
                      },
                    }}
                    height={300}
                  />
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, height: 340, position: 'relative', overflow: 'hidden' }}>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, fontSize: '1.5rem' }}>Expenses by Category</Typography>
                  <Pie
                    data={{
                      labels: expensesByCategory.map(e => e.category),
                      datasets: [
                        {
                          data: expensesByCategory.map(e => e.amount),
                          backgroundColor: ['#f59e0b', '#8b5cf6', '#3b82f6', '#64748b'],
                          borderWidth: 2,
                          borderColor: '#fff',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: { font: { size: 12 } }
                        },
                      },
                    }}
                    height={300}
                  />
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, height: 340, position: 'relative', overflow: 'hidden' }}>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, fontSize: '1.5rem' }}>Top 5 Clients by Revenue</Typography>
                  <Bar
                    data={{
                      labels: topClients.map(c => c.name),
                      datasets: [
                        {
                          label: 'Revenue',
                          data: topClients.map(c => c.revenue),
                          backgroundColor: 'rgba(139, 92, 246, 0.8)',
                          borderColor: '#8b5cf6',
                          borderWidth: 2,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                          labels: { font: { size: 12 } }
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: { font: { size: 11 } },
                        },
                      },
                    }}
                    height={300}
                  />
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper sx={{ p: 3, height: 340, position: 'relative', overflow: 'hidden' }}>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, fontSize: '1.5rem' }}>Top 5 Expenses</Typography>
                  <Bar
                    data={{
                      labels: topExpenses.map(e => e.category),
                      datasets: [
                        {
                          label: 'Amount',
                          data: topExpenses.map(e => e.amount),
                          backgroundColor: 'rgba(239, 68, 68, 0.8)',
                          borderColor: '#ef4444',
                          borderWidth: 2,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                          labels: { font: { size: 12 } }
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: { font: { size: 11 } },
                        },
                      },
                    }}
                    height={300}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4, borderWidth: 2 }} />

          {/* Order Reports Section */}
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 3, 
                color: 'secondary.main', 
                fontWeight: 'bold',
                textAlign: 'center',
                pb: 2,
                borderBottom: '3px solid',
                borderColor: 'secondary.main'
              }}
            >
              📦 Order Reports
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, height: 340, position: 'relative', overflow: 'hidden' }}>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, fontSize: '1.5rem' }}>Orders by Status</Typography>
                  <Pie
                    data={{
                      labels: ordersByStatus.map(s => s.status),
                      datasets: [
                        {
                          data: ordersByStatus.map(s => s.count),
                          backgroundColor: ['#10b981', '#f59e0b', '#3b82f6', '#ef4444'],
                          borderWidth: 2,
                          borderColor: '#fff',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: { font: { size: 12 } }
                        },
                      },
                    }}
                    height={300}
                  />
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3, height: 340, position: 'relative', overflow: 'hidden' }}>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, fontSize: '1.5rem' }}>Orders by Month</Typography>
                  <Line
                    data={{
                      labels: orderMonths,
                      datasets: [
                        {
                          label: 'Orders',
                          data: ordersByMonth,
                          backgroundColor: 'rgba(139, 92, 246, 0.2)',
                          borderColor: '#8b5cf6',
                          borderWidth: 3,
                          tension: 0.4,
                          fill: true,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top',
                          labels: { font: { size: 12 } }
                        },
                      },
                      scales: {
                        x: {
                          ticks: { font: { size: 11 }, maxRotation: 45, minRotation: 30 },
                        },
                        y: {
                          beginAtZero: true,
                          ticks: { font: { size: 11 } },
                        },
                      },
                    }}
                    height={300}
                  />
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: 340, position: 'relative', overflow: 'hidden' }}>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, fontSize: '1.5rem' }}>Top 5 Drivers by Orders</Typography>
                  <Bar
                    data={{
                      labels: topDrivers.map(d => d.name),
                      datasets: [
                        {
                          label: 'Orders',
                          data: topDrivers.map(d => d.orders),
                          backgroundColor: 'rgba(59, 130, 246, 0.8)',
                          borderColor: '#3b82f6',
                          borderWidth: 2,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                          labels: { font: { size: 12 } }
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: { font: { size: 11 } },
                        },
                      },
                    }}
                    height={300}
                  />
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: 340, position: 'relative', overflow: 'hidden' }}>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, fontSize: '1.5rem' }}>Completed Orders by Client</Typography>
                  <Bar
                    data={{
                      labels: completedOrdersByClient.map(c => c.name),
                      datasets: [
                        {
                          label: 'Completed Orders',
                          data: completedOrdersByClient.map(c => c.orders),
                          backgroundColor: 'rgba(16, 185, 129, 0.8)',
                          borderColor: '#10b981',
                          borderWidth: 2,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                          labels: { font: { size: 12 } }
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: { font: { size: 11 } },
                        },
                      },
                    }}
                    height={300}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
} 